import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmActivitiesApi, crmContactsApi, crmOrgsApi, crmDealsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import Modal from '../Modal';
import { invalidateCrm } from '../../lib/queryCache';
import type { CrmActivity } from '../../types';

interface Props {
  activity?: CrmActivity;
  defaults?: { contactId?: string; dealId?: string; organizationId?: string };
  onClose: () => void;
  onSaved: () => void;
}

export default function CrmActivityForm({ activity, defaults, onClose, onSaved }: Props) {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [form, setForm] = useState({
    type: activity?.type ?? 'call',
    subject: activity?.subject ?? '',
    description: activity?.description ?? '',
    contactId: activity?.contactId ?? defaults?.contactId ?? '',
    dealId: activity?.dealId ?? defaults?.dealId ?? '',
    organizationId: activity?.organizationId ?? defaults?.organizationId ?? '',
    dueAt: activity?.dueAt ? activity.dueAt.slice(0, 16) : '',
    durationMinutes: activity?.durationMinutes?.toString() ?? '',
    outcome: activity?.outcome ?? '',
  });
  const [error, setError] = useState('');

  const { data: contacts } = useQuery({ queryKey: ['crm-contacts-pick', cid], queryFn: () => crmContactsApi.list({ limit: 200 }), enabled: !!cid });
  const { data: orgs } = useQuery({ queryKey: ['crm-orgs-pick', cid], queryFn: () => crmOrgsApi.list({ limit: 200 }), enabled: !!cid });
  const { data: deals } = useQuery({ queryKey: ['crm-deals-pick', cid], queryFn: () => crmDealsApi.list({ limit: 200 }), enabled: !!cid });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: any = {
        type: form.type,
        subject: form.subject,
        description: form.description || undefined,
        contactId: form.contactId || undefined,
        dealId: form.dealId || undefined,
        organizationId: form.organizationId || undefined,
        dueAt: form.dueAt || undefined,
        durationMinutes: form.durationMinutes ? parseInt(form.durationMinutes) : undefined,
        outcome: form.outcome || undefined,
      };
      return activity ? crmActivitiesApi.update(activity.id, payload) : crmActivitiesApi.create(payload);
    },
    onSuccess: () => { invalidateCrm(queryClient); onSaved(); },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  });

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.subject || !form.type}>
        {saveMutation.isPending ? 'Saving…' : activity ? 'Save Changes' : 'Log Activity'}
      </button>
    </>
  );

  return (
    <Modal open onClose={onClose} title={activity ? 'Edit Activity' : 'Log Activity'} width={560} busy={saveMutation.isPending} footer={footer}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {error && <div className="text-[13px] text-err bg-primary-muted rounded-lg p-3">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Type *</label>
            <select className="select w-full" value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="call">Call</option>
              <option value="email">Email</option>
              <option value="meeting">Meeting</option>
              <option value="note">Note</option>
              <option value="task">Task</option>
            </select>
          </div>
          <div>
            <label className="label">Due Date</label>
            <input className="input w-full" type="datetime-local" value={form.dueAt} onChange={e => set('dueAt', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="label">Subject *</label>
          <input className="input w-full" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Activity subject" />
        </div>

        <div>
          <label className="label">Description</label>
          <textarea className="input w-full resize-y h-20" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Details…" />
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="label">Contact</label>
            <select className="select w-full" value={form.contactId} onChange={e => set('contactId', e.target.value)}>
              <option value="">— none —</option>
              {contacts?.contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Organization</label>
            <select className="select w-full" value={form.organizationId} onChange={e => set('organizationId', e.target.value)}>
              <option value="">— none —</option>
              {orgs?.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Deal</label>
            <select className="select w-full" value={form.dealId} onChange={e => set('dealId', e.target.value)}>
              <option value="">— none —</option>
              {deals?.deals.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Duration (minutes)</label>
            <input className="input w-full" type="number" min="0" value={form.durationMinutes} onChange={e => set('durationMinutes', e.target.value)} placeholder="0" />
          </div>
        </div>

        <div>
          <label className="label">Outcome</label>
          <input className="input w-full" value={form.outcome} onChange={e => set('outcome', e.target.value)} placeholder="Result or notes…" />
        </div>
      </div>
    </Modal>
  );
}
